import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, PackageCheck } from "lucide-react";
import MixSourcePicker from "@/components/exportaudio/MixSourcePicker";
import StemsPicker from "@/components/exportaudio/StemsPicker";
import useExportSong from "@/components/exportaudio/useExportSong";
import { createExport, mb } from "@/lib/exportAudioApi";
import { safeApiError } from "@/lib/safeErrorText";
import { clock } from "@/lib/voiceSongsApi";

const WAV_BYTES_PER_SEC = 48000 * 6;

function Toggle({ id, on, label, sub, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={on} data-testid={`export-format-${id}`} onClick={() => onChange(!on)}
      className={`flex-1 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ${on ? "border-sky-300/50 bg-sky-400/10" : "border-white/10 bg-black/25 text-white/55 hover:text-white"}`}>
      <span className="block text-[13px] font-semibold">{label}</span>
      <span className="block text-[11px] text-white/45">{sub}</span>
    </button>
  );
}

function wavCount(stems, set, wav) {
  if (!set || stems === "none") return wav ? 1 : 0;
  return (wav ? 1 : 0) + (stems !== "all" ? set.groups.length : 0) + (stems !== "groups" ? set.stems.length : 0);
}

/** What goes in the pack for one song: which full mix, WAV / MP3, which stems — then start it. */
export default function ExportBuilder({ songId, onStarted }) {
  const { detail, error, refresh, splitting } = useExportSong(songId);
  const [mix, setMix] = useState(null);
  const [wav, setWav] = useState(true);
  const [mp3, setMp3] = useState(true);
  const [stems, setStems] = useState("groups");
  const [setId, setSetId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!detail) return;
    setMix((m) => m ?? (detail.mixes[0]?.id || "song"));
    setSetId((s) => (detail.sets.some((x) => x.id === s) ? s : detail.sets[0]?.id || null));
  }, [detail]);
  if (error) return <p data-testid="export-song-error" className="text-[14px] text-rose-200">{safeApiError(error)}</p>;
  if (!detail || !mix) return <Loader2 className="h-6 w-6 animate-spin text-white/40" />;
  const song = detail.song;
  const set = detail.sets.find((x) => x.id === setId) || null;
  const effStems = set ? stems : "none";
  const files = wavCount(effStems, set, wav);
  const nothing = !wav && !mp3 && effStems === "none";
  const size = files * (song.duration || 0) * WAV_BYTES_PER_SEC + (mp3 ? (song.duration || 0) * 40000 : 0);
  const start = async () => {
    setBusy(true);
    try {
      await createExport({ song_id: songId, mix, wav, mp3, stems: effStems, set_id: effStems === "none" ? null : setId });
      toast.success("Making your export pack", { description: "It appears below — download it when it's ready." });
      onStarted();
    } catch (e) { toast.error("Couldn't start the export", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="export-builder" className="space-y-6 rounded-3xl border border-sky-300/20 bg-[#0b0f1c]/85 p-5 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-testid="export-song-title" className="truncate font-display text-xl font-semibold">{song.title}</h2>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.14em] text-white/45">
            {clock(song.duration)}{song.tempo?.bpm ? ` · ${Math.round(song.tempo.bpm)} BPM` : ""}{song.tempo?.tonic ? ` · ${song.tempo.tonic} ${song.tempo.mode || ""}` : ""}
          </p>
        </div>
        <Link to={`/create/song/${song.id}`} data-testid="export-open-song" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-white/50 hover:text-white">Open song <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
      <MixSourcePicker mixes={detail.mixes} value={mix} onChange={setMix} songId={song.id} />
      <div>
        <h3 className="mb-2 font-display text-[15px] font-semibold">Full mix files</h3>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Toggle id="wav" on={wav} onChange={setWav} label="WAV · 24-bit 48 kHz" sub="Studio quality, for Studio One and release" />
          <Toggle id="mp3" on={mp3} onChange={setMp3} label="MP3 · 320 kbps" sub="Small file for sharing" />
        </div>
      </div>
      <StemsPicker detail={detail} set={set} onSet={setSetId} value={stems} onChange={setStems} splitting={splitting} refresh={refresh} />
      <div className="space-y-2">
        <button type="button" data-testid="export-start" onClick={start} disabled={busy || nothing}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-[14px] font-semibold text-black transition-transform duration-200 hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Make the export pack
        </button>
        <p data-testid="export-estimate" className="text-center text-[11px] text-white/45">
          {nothing ? "Pick at least one thing to export." : `${files} WAV file${files === 1 ? "" : "s"}${mp3 ? " + MP3" : ""} · about ${mb(size)} · all the same length from 0:00`}
        </p>
      </div>
    </section>
  );
}
